import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  Input,
  Label,
  ScrollArea,
  Textarea
} from '@repo/ui'
import * as Sentry from '@sentry/react'
import { useForm } from '@tanstack/react-form'
import { useRouter } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { z } from 'zod'
import type { OrgUser } from '@/api/org/users'
import { createProject, updateProject } from '@/api/project/create'
import { addUserToProject, removeUserFromProject } from '@/api/project/members'
import type { ProjectApiResponse } from '@/api/project/list'
import { zodFieldValidator } from '@/common/parseDataType'

type ProjectDialogProps = {
  open: boolean
  onClose: () => void
  orgUsers: Array<OrgUser>
} & (
  | { mode: 'create'; currentOrgSlug: string; project?: undefined }
  | { mode: 'edit'; project: ProjectApiResponse; currentOrgSlug?: undefined }
)

export function ProjectDialog({ open, onClose, orgUsers, ...modeProps }: ProjectDialogProps) {
  const isEdit = modeProps.mode === 'edit'
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Workspace' : 'New Workspace'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? 'Edit workspace details' : 'Create a new workspace'}
          </DialogDescription>
        </DialogHeader>
        <ProjectForm
          key={open ? 'open' : 'closed'}
          orgUsers={orgUsers}
          onComplete={onClose}
          onCancel={onClose}
          {...modeProps}
        />
      </DialogContent>
    </Dialog>
  )
}

type ProjectFormProps = {
  orgUsers: Array<OrgUser>
  onComplete: () => void
  onCancel?: () => void
} & (
  | { mode: 'create'; currentOrgSlug: string; project?: undefined }
  | { mode: 'edit'; project: ProjectApiResponse; currentOrgSlug?: undefined }
)

export function ProjectForm({ orgUsers, onComplete, onCancel, ...modeProps }: ProjectFormProps) {
  const isEdit = modeProps.mode === 'edit'
  const project = modeProps.mode === 'edit' ? modeProps.project : undefined

  const router = useRouter()
  const currentUserId = router.options.context.user.userOrgProfile?.uid
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [memberSearch, setMemberSearch] = useState('')

  const initialMemberIds = project
    ? new Set(project.members.map((m) => m.userId))
    : currentUserId
      ? new Set([currentUserId])
      : new Set<number>()

  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(() => new Set(initialMemberIds))

  const filteredUsers = useMemo(() => {
    if (!memberSearch) return orgUsers
    const query = memberSearch.toLowerCase()
    return orgUsers.filter(
      (u) =>
        u.firstName.toLowerCase().includes(query) ||
        u.surname.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
    )
  }, [orgUsers, memberSearch])

  const form = useForm({
    defaultValues: {
      title: project?.title ?? '',
      description: project?.description ?? ''
    },
    onSubmit: async ({ value }) => {
      setSubmitError(null)
      try {
        if (isEdit && project) {
          await submitEdit(value, project, selectedUserIds)
        } else if (!isEdit) {
          await submitCreate(value, selectedUserIds, currentUserId, modeProps.currentOrgSlug)
        }
      } catch (err) {
        Sentry.captureException(err, { extra: { userId: currentUserId, title: value.title } })
        setSubmitError(err instanceof Error ? err.message : 'An error occurred')
      }
    }
  })

  async function submitCreate(
    value: { title: string; description: string },
    memberIds: Set<number>,
    currentUid: number | undefined,
    orgSlug: string
  ) {
    const newProject = await createProject({
      title: value.title,
      description: value.description,
      colourScheme: ''
    })

    const otherMemberIds = [...memberIds].filter((id) => id !== currentUid)
    const memberErrors: Array<string> = []
    await Promise.all(
      otherMemberIds.map((userId) =>
        addUserToProject(newProject.projectId, userId).catch((err) => {
          memberErrors.push(err instanceof Error ? err.message : 'Failed to add a member')
        })
      )
    )

    await router.invalidate()

    if (memberErrors.length > 0) {
      setSubmitError(`Workspace created, but some members could not be added: ${memberErrors.join(', ')}`)
      return
    }

    router.navigate({
      to: '/$orgSlug/$projectId',
      params: { orgSlug: orgSlug, projectId: String(newProject.projectId) }
    })
    handleComplete()
  }

  async function submitEdit(
    value: { title: string; description: string },
    existingProject: ProjectApiResponse,
    memberIds: Set<number>
  ) {
    await updateProject(existingProject.projectId, {
      title: value.title,
      description: value.description,
      colourScheme: existingProject.colourScheme
    })

    const existingMemberIds = new Set(existingProject.members.map((m) => m.userId))
    const toAdd = [...memberIds].filter((id) => !existingMemberIds.has(id))
    const toRemove = [...existingMemberIds].filter((id) => !memberIds.has(id))

    const memberErrors: Array<string> = []
    await Promise.all([
      ...toAdd.map((userId) =>
        addUserToProject(existingProject.projectId, userId).catch((err) => {
          memberErrors.push(err instanceof Error ? err.message : 'Failed to add a member')
        })
      ),
      ...toRemove.map((userId) =>
        removeUserFromProject(existingProject.projectId, userId).catch((err) => {
          memberErrors.push(err instanceof Error ? err.message : 'Failed to remove a member')
        })
      )
    ])

    await router.invalidate()

    if (memberErrors.length > 0) {
      setSubmitError(`Workspace saved, but some member changes failed: ${memberErrors.join(', ')}`)
      return
    }

    handleComplete()
  }

  function resetForm() {
    form.reset()
    setSubmitError(null)
    setSelectedUserIds(new Set(initialMemberIds))
    setMemberSearch('')
  }

  function handleComplete() {
    resetForm()
    onComplete()
  }

  function handleCancel() {
    resetForm()
    onCancel?.()
  }

  function toggleUser(userId: number) {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  return (
    <>
      {submitError && (
        <p role="alert" className="text-sm text-destructive">
          {submitError}
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
        className="space-y-4"
      >
        <form.Field name="title" validators={{ onChange: zodFieldValidator(z.string().min(1, 'Title is required')) }}>
          {(field) => (
            <Field>
              <Label htmlFor="project-title">Title</Label>
              <Input
                id="project-title"
                placeholder="Workspace title"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={form.state.isSubmitting}
              />
              <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
            </Field>
          )}
        </form.Field>

        <form.Field name="description">
          {(field) => (
            <Field>
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                placeholder="Optional description"
                rows={3}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={form.state.isSubmitting}
                className="resize-none"
              />
            </Field>
          )}
        </form.Field>

        <Field>
          <Label>Members</Label>
          {orgUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users found</p>
          ) : (
            <div className="rounded-md border">
              <div className="relative border-b px-2 py-1.5">
                <Search className="absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="h-8 pl-8 text-xs"
                />
              </div>
              <ScrollArea className="h-48">
                <div className="space-y-1 p-2">
                  {filteredUsers.map((user) => {
                    const isCurrentUser = user.userId === currentUserId
                    return (
                      <label
                        key={user.userId}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                      >
                        <Checkbox
                          checked={selectedUserIds.has(user.userId)}
                          onCheckedChange={() => toggleUser(user.userId)}
                          disabled={isCurrentUser || form.state.isSubmitting}
                        />
                        <span>
                          {user.firstName}
                          {user.surname ? ` ${user.surname}` : ''}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground">{user.email}</span>
                        {isCurrentUser && <span className="text-xs text-muted-foreground">(you)</span>}
                      </label>
                    )
                  })}
                </div>
              </ScrollArea>
            </div>
          )}
        </Field>

        <DialogFooter>
          {onCancel !== undefined && (
            <Button type="button" variant="outline" onClick={handleCancel} disabled={form.state.isSubmitting}>
              Cancel
            </Button>
          )}
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save' : 'Create'}
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      </form>
    </>
  )
}
